// Publicar com a checagem de avaliação. Produção mostra a evidência (ou o que falta) antes de
// trocar o canal; os outros canais publicam direto.
import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { AlertTriangle, CheckCircle2, FlaskConical, Rocket, ShieldAlert } from "lucide-react";
import { api, invalidate, toast, toastError } from "../lib";
import { Badge, Button, Modal, PassBar } from "../ui";

type Cell = { eval: string; cell: string; passed: number; total: number; avgScore: number; errors: number };
export type DeployCheck = {
  channel: string;
  version: number;
  current: number | null;
  ok: boolean;
  reasons: string[];
  warnings: string[];
  evidence: { candidate: Cell; production: Cell | null; paired: { gains: string[]; losses: string[]; pValue: number } | null; trials: number; minRuns: number } | null;
  suggest: { versions: number[]; trials: number } | null;
};

type State = { channel: string; version: number; check: DeployCheck | null; error?: string };

// A mensagem do servidor termina com o comando da CLI; aqui o botão faz o papel dele.
const withoutCli = (s: string) => s.replace(/\s*Rode: atelie .*$/, "");

export function useDeploy(p: string, t: string) {
  const path = `/api/projects/${p}/targets/${t}`;
  const [state, setState] = useState<State | null>(null);
  const [busy, setBusy] = useState<"deploy" | "eval" | null>(null);
  const [, nav] = useLocation();

  const publish = async (channel: string, version: number, force = false) => {
    setBusy("deploy");
    try {
      const d = await api.post(path + "/deploy", { channel, version, force });
      toast(`v${version} no ar em ${channel}`, d.forced ? "publicada sem a evidência exigida; ficou marcada no histórico" : `endpoint atualizado em ${d.liveMs} ms`);
      invalidate(path);
      invalidate("/api/projects");
      setState(null);
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };

  const deploy = async (channel: string, version: number) => {
    if (channel !== "production") return publish(channel, version);
    setState({ channel, version, check: null });
    try {
      const check = await api.get<DeployCheck>(`${path}/deploy-check?version=${version}&channel=${channel}`);
      setState({ channel, version, check });
    } catch (e: any) {
      setState({ channel, version, check: null, error: e.message });
    }
  };

  const runEval = async () => {
    const s = state?.check?.suggest;
    if (!s) return;
    setBusy("eval");
    try {
      const r = await api.post(`${path}/evals`, { versions: s.versions, models: [], trials: s.trials });
      setState(null);
      nav(`/p/${p}/e/${r.id}`);
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(null);
    }
  };

  const dialog = state ? (
    <DeployDialog p={p} state={state} busy={busy} onClose={() => setState(null)} onPublish={(force) => publish(state.channel, state.version, force)} onEval={runEval} />
  ) : null;
  return { deploy, dialog };
}

function DeployDialog({ p, state, busy, onClose, onPublish, onEval }: { p: string; state: State; busy: "deploy" | "eval" | null; onClose: () => void; onPublish: (force: boolean) => void; onEval: () => void }) {
  const { version, check } = state;
  const ev = check?.evidence;
  const footer = !check ? null : check.ok ? (
    <>
      <Button variant="ghost" onClick={onClose}>Cancelar</Button>
      <Button variant="green" icon={<Rocket size={13} />} loading={busy === "deploy"} onClick={() => onPublish(false)}>Publicar</Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={onClose}>Cancelar</Button>
      <Button variant="danger" loading={busy === "deploy"} onClick={() => onPublish(true)}>Publicar mesmo assim</Button>
      {check.suggest && (
        <Button variant="primary" icon={<FlaskConical size={13} />} loading={busy === "eval"} onClick={onEval}>
          Rodar avaliação{check.suggest.trials > 1 ? `, ${check.suggest.trials} trials` : ""}
        </Button>
      )}
    </>
  );
  const okText = ev ? (ev.production ? "Não piorou em relação à versão em produção." : "Primeira versão em produção, com avaliação.") : check?.warnings[0] ?? "Liberada.";
  const warnings = check ? (ev || !check.ok ? check.warnings : check.warnings.slice(1)) : [];
  return (
    <Modal open onClose={onClose} width={600} title={<span className="row" style={{ gap: 8 }}><Rocket size={15} />Publicar v{version} em produção</span>} footer={footer}>
      {!check && !state.error && <div className="faint">Conferindo as avaliações…</div>}
      {state.error && <div className="banner err"><ShieldAlert size={15} /><span className="grow">{state.error}</span></div>}
      {check &&
        (check.ok ? (
          <div className="banner ok"><CheckCircle2 size={15} /><span className="grow">{okText}</span></div>
        ) : (
          <div className="banner err">
            <ShieldAlert size={15} />
            <div className="grow col" style={{ gap: 4 }}>{check.reasons.map((r) => <span key={r}>{withoutCli(r)}</span>)}</div>
          </div>
        ))}
      {ev && (
        <div className="col" style={{ gap: 8 }}>
          <div className="card">
            <table className="table">
              <thead><tr><th>Versão</th><th>Casos que passaram</th><th>Nota</th></tr></thead>
              <tbody>
                <tr>
                  <td><span className="row" style={{ gap: 8 }}><span className="mono">v{version}</span><Badge tone="blue">candidata</Badge></span></td>
                  <td><PassBar passed={ev.candidate.passed} total={ev.candidate.total} /></td>
                  <td className="mono num">{ev.candidate.avgScore.toFixed(2)}</td>
                </tr>
                {ev.production && (
                  <tr>
                    <td><span className="row" style={{ gap: 8 }}><span className="mono">v{check!.current}</span><Badge tone="green">em produção</Badge></span></td>
                    <td><PassBar passed={ev.production.passed} total={ev.production.total} /></td>
                    <td className="mono num">{ev.production.avgScore.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="faint" style={{ fontSize: 12.5 }}>
            {ev.paired ? `Caso a caso: melhorou em ${ev.paired.gains.length}, piorou em ${ev.paired.losses.length}` : "Sem versão em produção para comparar"}
            {" · "}
            {ev.trials} {ev.trials > 1 ? "trials" : "trial"} por caso · <Link href={`/p/${p}/e/${ev.candidate.eval}`}>ver avaliação</Link>
          </div>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="banner">
          <AlertTriangle size={15} />
          <div className="grow col" style={{ gap: 4 }}>{warnings.map((w) => <span key={w}>{w}</span>)}</div>
        </div>
      )}
    </Modal>
  );
}
